import { Avatar, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { PagoClienteResponse } from '@aid/shared';
import { Link } from 'react-router-dom';
import { PagoStatusTag } from './PagoStatusTag';
import { PagosTableActions } from './PagosTableActions';
import { StatusTag } from '../../../components/StatusTag';
import { formatearPeriodo } from '../../../lib/date';
import { formatCurrency } from '../../../lib/format';
import { DATE_FORMAT } from '../../../lib/date';
import dayjs from 'dayjs';
import { getPagoInitials, rangoCubierto } from '../lib/pago-table-helpers';
import type { PagosTableGroupBy, PagosTranslate } from './pago-table-types';
import { PAGOS_TABLE_GROUP } from './pago-table-types';
import { CLIENTE_TABS } from '../../clientes/cliente-tabs';
import { serverSortProps, type ServerSortState } from '../../../lib/server-sort';

export interface PagosTableColumnsOptions {
  t: PagosTranslate;
  showClient: boolean;
  showUser: boolean;
  groupBy: PagosTableGroupBy;
  canAnular: boolean;
  onDetail: (id: number) => void;
  onAnular: (pago: PagoClienteResponse) => void;
  onViewBoleta: (id: number) => void;
  primaryColor: string;
  sort?: ServerSortState;
}

function PagoStatusTags({ pago }: { pago: PagoClienteResponse }) {
  return <PagoStatusTag motivoAnulacion={pago.motivoAnulacion} idEstadoPago={pago.idEstadoPago} />;
}

export function createPagosTableColumns({
  t,
  showClient,
  showUser,
  groupBy,
  canAnular,
  onDetail,
  onAnular,
  onViewBoleta,
  primaryColor,
  sort = {},
}: PagosTableColumnsOptions): ColumnsType<PagoClienteResponse> {
  const columns: ColumnsType<PagoClienteResponse> = [
    ...(!showClient
      ? [
          {
            title: t('common.id'),
            key: 'id',
            ...serverSortProps('id', sort),
            width: 130,
            render: (_value: unknown, record: PagoClienteResponse) =>
              t('pages.clientes.actividad.pagoId', { id: record.id }),
          },
        ]
      : []),
    ...(showClient
      ? [
          {
            title: t('pages.pagos.cliente'),
            key: 'cliente',
            ...serverSortProps('cliente', sort),
            width: 210,
            ellipsis: true,
            render: (_value: unknown, record: PagoClienteResponse) => {
              const label = record.cliente
                ? `${record.cliente.nombre ?? ''} (#${record.cliente.id})`
                : `#${record.idCliente}`;
              return (
                <Link
                  to={`/clientes/${record.idCliente}/${CLIENTE_TABS.PAGOS}`}
                  title={label}
                  aria-label={label}
                  style={{ color: 'inherit' }}
                >
                  <span>
                    <Avatar
                      size={24}
                      style={{
                        background: primaryColor,
                        verticalAlign: 'middle',
                        marginRight: 8,
                        fontSize: 12,
                      }}
                      aria-hidden
                    >
                      {getPagoInitials(record.cliente?.nombre)}
                    </Avatar>
                    {label}
                  </span>
                </Link>
              );
            },
          },
        ]
      : []),
    {
      title: t('pages.pagos.fechaCreacion'),
      key: 'fechaCreacion',
      ...serverSortProps('fechaCreacion', sort),
      width: 120,
      render: (_value: unknown, record: PagoClienteResponse) =>
        record.fechaCreacion ? dayjs(record.fechaCreacion).format(DATE_FORMAT) : '-',
    },
    {
      title: t('pages.clientes.modal.periodoCubierto'),
      key: 'periodoCubierto',
      width: 180,
      render: (_value: unknown, record: PagoClienteResponse) => {
        const [inicio, fin] = rangoCubierto(record);
        return formatearPeriodo(inicio, fin) ?? '-';
      },
    },
    {
      title: t('pages.pagos.estado'),
      key: 'estadoPago',
      ...serverSortProps('estadoPago', sort),
      width: 130,
      render: (_value: unknown, record: PagoClienteResponse) => <PagoStatusTags pago={record} />,
    },
    {
      title: t('pages.pagos.valor'),
      key: 'valor',
      ...serverSortProps('valor', sort),
      width: 150,
      render: (_value: unknown, record: PagoClienteResponse) => (
        <Space wrap size={[6, 6]}>
          <span>{formatCurrency(record.valor)}</span>
          {record.modalidad === 'ADELANTADO' && record.descuentoValor ? (
            <StatusTag tone="success">
              {t('pages.clientes.actividad.descuentoTag', {
                valor: formatCurrency(record.descuentoValor),
              })}
            </StatusTag>
          ) : null}
        </Space>
      ),
    },
    {
      title: t('pages.pagos.comentarios'),
      key: 'comentarios',
      width: 220,
      // Multi-line: no ellipsis so long operator comments wrap inside the
      // cell instead of being truncated (user decision 2026-09-29).
      render: (_value: unknown, record: PagoClienteResponse) => record.comentarios ?? '-',
    },
    ...(showUser
      ? [
          {
            title: t('pages.pagos.registroUsuario'),
            key: 'usuario',
            width: 160,
            ellipsis: true,
            render: (_value: unknown, record: PagoClienteResponse) => {
              // The joined user ref wins; legacy rows without it fall back to
              // the last modifier username (or a placeholder when absent).
              const label = record.usuario
                ? record.usuario.nombre || record.usuario.username
                : (record.usuarioModifica ?? '-');
              return (
                <span title={record.usuario?.username} aria-label={label}>
                  {label}
                </span>
              );
            },
          },
        ]
      : []),
    {
      title: t('common.actions'),
      key: 'actions',
      width: 64,
      render: (_value: unknown, record: PagoClienteResponse) => (
        <PagosTableActions
          pago={record}
          canAnular={canAnular}
          onDetail={onDetail}
          onAnular={onAnular}
          onViewBoleta={onViewBoleta}
        />
      ),
    },
  ];

  return columns.filter((column) => {
    if (groupBy === PAGOS_TABLE_GROUP.CLIENTE && column.key === 'cliente') return false;
    if (groupBy === PAGOS_TABLE_GROUP.ESTADO && column.key === 'estadoPago') return false;
    return true;
  });
}
